import { Lock, Loop } from '@mui/icons-material';
import { Box, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import changeMapCertificate from '@src/APIs/graphQl/Certificate/changeMapCertificate';
import { useMapCertificateDialog } from '@src/App/Components/Dialogs/MapCertificateDialog/MapCertificateDialog';
import CertificateOverlay from '@src/App/Components/Overlays/CertificateOverlay/CertificateOverlay';
import type { AppState } from '@store/store';
import { CircleMinus, CirclePlus, Settings2 } from 'lucide-react';
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ItemBadge from '../../ItemBadge/ItemBadge';
import Item from '../Item/Item';
import type { IItemAction } from '../Item/ItemAction';

interface CertificateItemProps {
    id: number;
    title: string;
    startDate?: Date;
    endDate?: Date;
    disableOverlay?: boolean;
    lockedBy?: string;
    negative?: boolean;
    onClick?: string | (() => void);
}

/**
 * Represents a certificate item.
 *
 * @param {CertificateItemProps} param - The parameters for the certificate item.
 * @param {string} param.title - The title of the certificate item.
 * @param {string} param.id - The ID of the certificate item.
 * @param {boolean} param.disableOverlay - Indicates if the overlay is disabled for the certificate item.
 * @param {string} param.lockedBy - The user who has locked the certificate item.
 * @param {Function} param.onClick - The function to be called when the certificate item is clicked.
 *
 * @returns {JSX.Element} The certificate item as a JSX element.
 */
function CertificateItem({ title, id, disableOverlay, lockedBy, endDate, negative, onClick }: CertificateItemProps) {
    const theme = useTheme();
    const navigate = useNavigate();
    const mapCertificateDialog = useMapCertificateDialog();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).certificates;
    const [updating, setUpdating] = React.useState('');
    const [overlayOpen, setOverlayOpen] = React.useState(false);
    const employee = useSelector((state: AppState) => state.employee);
    const own = employee.certificates.find((oc) => oc.id === id);

    const now = new Date();
    const twoMonthsFromNow = new Date();
    twoMonthsFromNow.setMonth(twoMonthsFromNow.getMonth() + 2);

    let status = 'active';
    let level = 3; // Primary color (standard)
    let percent = 75;

    if (endDate?.getTime) {
        const daysDifference = (endDate.getTime() - now.getTime()) / (24 * 60 * 60 * 1000);

        if (daysDifference <= 0) {
            status = 'expired';
            level = 1; // Inactive level
            percent = 0;
        } else if (daysDifference <= 60) {
            // 2 months
            status = 'expiring';
            level = 2; // Warning level
            percent = 20 + 55 * (daysDifference / 365);
        } else {
            status = 'active';
            level = 3; // Primary color
            percent = 75;
        }
    }

    // Check if it's the newest certificate (secondary color)
    const isNewest =
        employee.certificates &&
        employee.certificates.length > 0 &&
        [...employee.certificates].sort((a, b) => (b.startDate ? new Date(b.startDate).getTime() : 0) - (a.startDate ? new Date(a.startDate).getTime() : 0))[0]
            ?.id === id;

    if (isNewest && status === 'active') {
        level = 4; // Use level 4 for newest (will be styled with secondary color)
    }

    if (negative) {
        percent = percent * -1;
    }

    const actions: IItemAction[] = [];
    const laodingIcon = <Loop fontSize="small" />;

    if (id) {
        if (own) {
            //Nicht Edit Mode und ist im System bekannt Und Verknüpft mit Emp
            actions.push({
                id: 'remove',
                icon: updating === 'remove' ? laodingIcon : <CircleMinus size={16} />,
                helper: langStrings.removeButtonHelper,
                text: langStrings.removeButtonTitle,
                onClick: () => {
                    setUpdating('remove');
                    changeMapCertificate(id, false, undefined, undefined).finally(() => {
                        setUpdating('');
                    });
                },
            });
            actions.push({
                id: 'edit',
                icon: updating === 'edit' ? laodingIcon : <Settings2 size={16} />,
                text: langStrings.editButtonTitle,
                helper: langStrings.editButtonHelper,
                onClick: () => {
                    setUpdating('edit');
                    mapCertificateDialog(id).finally(() => {
                        setUpdating('');
                    });
                },
            });
        } else {
            actions.push({
                id: 'add',
                icon: updating === 'add' ? laodingIcon : <CirclePlus size={16} />,
                text: langStrings.addButtonTitle,
                helper: langStrings.addButtonHelper,
                onClick: () => {
                    setUpdating('add');
                    mapCertificateDialog(id).finally(() => {
                        setUpdating('');
                    });
                },
            });
        }
    }

    const handleSingleClick = () => {
        if (id && !disableOverlay) {
            setOverlayOpen(true);
        }
    };

    const handleDoubleClick = () => {
        if (onClick) {
            if (typeof onClick === 'string') {
                navigate(onClick);
            } else {
                onClick();
            }
        }
    };

    const getStatusText = () => {
        switch (status) {
            case 'expired':
                return langStrings.oftenUsed.inactive;
            case 'expiring':
                return 'Läuft ab';
            case 'active':
                return isNewest ? 'Neuestes' : langStrings.oftenUsed.active;
            default:
                return langStrings.oftenUsed.active;
        }
    };

    return (
        <Item
            percent={percent}
            level={level}
            onClick={handleSingleClick}
            onDoubleClick={handleDoubleClick}
            actions={actions}
            leftContent={
                <ItemBadge
                    value={getStatusText()}
                    title={
                        langStrings.oftenUsed.active +
                        (endDate?.toLocaleDateString ? `, ${langStrings.endDate}: ${endDate.toLocaleDateString()}` : '')
                    }
                    style={{
                        backgroundColor:
                            status === 'active'
                                ? alpha(theme.palette.success.main, 0.1)
                                : status === 'expiring'
                                  ? alpha(theme.palette.warning.main, 0.1)
                                  : isNewest
                                    ? alpha(theme.palette.accent!.main, 0.1)
                                    : alpha(theme.palette.grey[500], 0.1),
                        color:
                            status === 'active'
                                ? theme.palette.success.main
                                : status === 'expiring'
                                  ? theme.palette.warning.main
                                  : isNewest
                                    ? theme.palette.accent!.main
                                    : theme.palette.grey[600],
                        border: `1px solid ${
                            status === 'active'
                                ? alpha(theme.palette.success.main, 0.2)
                                : status === 'expiring'
                                  ? alpha(theme.palette.warning.main, 0.2)
                                  : isNewest
                                    ? alpha(theme.palette.accent!.main, 0.2)
                                    : alpha(theme.palette.grey[500], 0.2)
                        }`,
                    }}
                />
            }
            rightContent={
                lockedBy && (
                    <Box title={`${langStrings.oftenUsed.lockedBy}: "${lockedBy}"`}>
                        <Lock fontSize="small" sx={{ color: theme.palette.warning.main }} />
                    </Box>
                )
            }
        >
            <CertificateOverlay
                key={id}
                disabled={false}
                certificateId={id}
                open={overlayOpen}
                onClose={() => setOverlayOpen(false)}
            >
                {title}
            </CertificateOverlay>
        </Item>
    );
}
export default CertificateItem;
